// Cart delivery-method controller — bulk-switches every cart line between
// subscription and one-time when the radios/frequency select in
// snippets/cart-delivery-method.liquid change.
//
// Loaded globally (see snippets/header-actions.liquid) so the delegated
// `change` listener is bound once on page load and survives cart-drawer
// morphs. It must NOT live as an inline <script> inside the snippet: that
// snippet only renders when the cart is non-empty, and morphed-in <script>
// nodes never execute — so on an empty-cart page load the listener would
// never attach until a full page reload.
(() => {
  const FORM_SELECTOR = '[data-cart-delivery-method]';
  const SOURCE = 'cart-delivery-method';
  const ERROR_HIDE_DELAY_MS = 4000;

  /**
   * @param {HTMLElement} form
   * @param {boolean} on
   */
  const setLoading = (form, on) => {
    if (on) {
      form.setAttribute('data-loading', '1');
    } else {
      form.removeAttribute('data-loading');
    }
    // Dim cart items too (class lives in snippets/cart-products.liquid).
    document.querySelectorAll('.cart-items__list, .cart-items__wrapper').forEach((el) => {
      el.classList.toggle('cart-items-disabled', !!on);
    });
  };

  /** @param {HTMLElement} form */
  const showError = (form) => {
    const err = /** @type {HTMLElement | null} */ (form.querySelector('[data-cdm-error]'));
    if (!err) return;
    err.hidden = false;
    window.clearTimeout(Number(err.dataset.cdmHideTimer));
    const id = window.setTimeout(() => { err.hidden = true; }, ERROR_HIDE_DELAY_MS);
    err.dataset.cdmHideTimer = String(id);
  };

  /** @returns {string[]} */
  const collectSectionIds = () => {
    /** @type {Set<string>} */
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = /** @type {HTMLElement} */ (el).dataset?.sectionId;
      if (id) ids.add(id);
    });
    return Array.from(ids);
  };

  /** @param {{ sections?: Record<string, string>, item_count?: number }} data */
  const dispatchCartUpdate = (data) => {
    // Shape matches assets/component-cart-items.js — handlers read event.detail.data.sections[sectionId].
    const event = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        data,
        source: SOURCE,
        sections: data.sections,
        itemCount: data.item_count,
      },
    });
    document.dispatchEvent(event);
  };

  /**
   * @param {HTMLElement} form
   * @returns {string | null}
   */
  const resolveTargetFrequency = (form) => {
    const checkedMode = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="cart-delivery-mode"]:checked')
    );
    if (!checkedMode) return null;
    if (checkedMode.value === 'subscription') {
      const select = /** @type {HTMLSelectElement | null} */ (form.querySelector('[data-freq-select]'));
      return select?.value || null;
    }
    return null;
  };

  /**
   * @param {HTMLElement} form
   * @returns {Record<string, Record<string, number>>}
   */
  const readLinePlans = (form) => {
    const el = form.querySelector('[data-cdm-line-plans]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '') || {};
    } catch (e) {
      return {};
    }
  };

  /**
   * @param {HTMLElement} form
   * @param {string | null} freqKey
   */
  const setAllSellingPlans = async (form, freqKey) => {
    setLoading(form, true);
    try {
      const linePlans = readLinePlans(form);
      const cartRes = await fetch('/cart.js');
      if (!cartRes.ok) throw new Error('cart.js fetch failed');
      const cart = await cartRes.json();

      for (const item of cart.items) {
        // Resolve THIS line's own plan for the chosen frequency. No match
        // (or one-time mode) → null clears the line back to one-time.
        const perFreq = linePlans[item.key];
        let planForLine = null;
        if (freqKey && perFreq && perFreq[freqKey] != null) {
          planForLine = perFreq[freqKey];
        }
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: item.quantity, selling_plan: planForLine }),
          });
        } catch (err) {
          console.warn('[cart-delivery-method] change.js failed for', item.key, err);
        }
      }

      // Trailing /cart/update.js refreshes sections even if every change.js
      // rejected, and persists the selection as a cart attribute so any
      // subsequent add-to-cart honors it (see snippets/header-actions.liquid).
      const sectionIds = collectSectionIds();
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: {},
          attributes: { gs_purchase_frequency: freqKey == null ? '' : String(freqKey) },
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!updateRes.ok) throw new Error('cart/update.js fetch failed');
      const data = await updateRes.json();

      dispatchCartUpdate(data);
    } catch (err) {
      console.error('[cart-delivery-method] bulk update failed', err);
      showError(form);
    } finally {
      setLoading(form, false);
    }
  };

  /** @param {Event} event */
  const onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const form = /** @type {HTMLElement | null} */ (target.closest(FORM_SELECTOR));
    if (!form) return;
    if (form.hasAttribute('data-loading')) return;

    const isMode = target.matches('input[name="cart-delivery-mode"]');
    const isFreq = target.matches('[data-freq-select]');
    if (!isMode && !isFreq) return;

    // Auto-flip to subscription — picking a frequency only makes sense there.
    if (isFreq) {
      const sub = /** @type {HTMLInputElement | null} */ (
        form.querySelector('input[name="cart-delivery-mode"][value="subscription"]')
      );
      if (sub && !sub.checked) {
        sub.checked = true;
      }
    }

    const freqKey = resolveTargetFrequency(form);
    setAllSellingPlans(form, freqKey);
  };

  document.addEventListener('change', onChange);
})();
